import type { MerchantApiClient } from "../../types.js";
import type {
  AnalyticsDashboardDto,
  AnalyticsCurrencySegment,
  AnalyticsFilters,
  AnalyticsFunnelStep,
  AnalyticsTimeSeriesPoint,
} from "./models.js";

interface DataEnvelope<T> {
  data: T;
  meta: { requestId: string };
}

interface AnalyticsSummaryDto {
  currency: string;
  requests: number;
  verified: number;
  reviewRequired: number;
  offers: number;
  accepted: number;
  ordered: number;
  recoveredRevenueMinor: number;
  discountCostMinor: number;
  refundedMinor: number;
  acceptanceRate: number;
  conversionRate: number;
}

export interface AnalyticsApi {
  getDashboard(filters: AnalyticsFilters): Promise<AnalyticsDashboardDto>;
  getTimeSeries(filters: AnalyticsFilters): Promise<AnalyticsTimeSeriesPoint[]>;
}

export function createAnalyticsApi(client: MerchantApiClient): AnalyticsApi {
  return {
    async getDashboard(filters) {
      const summaries = unwrap(
        await client.get<DataEnvelope<AnalyticsSummaryDto[]>>(
          `/v1/api/analytics/summary?${query(filters)}`,
        ),
      );
      return analyticsDashboardFromSummaries(filters, summaries);
    },
    async getTimeSeries(filters) {
      return unwrap(
        await client.get<DataEnvelope<AnalyticsTimeSeriesPoint[]>>(
          `/v1/api/analytics/timeseries?${query(filters)}`,
        ),
      );
    },
  };
}

function analyticsDashboardFromSummaries(
  filters: AnalyticsFilters,
  summaries: AnalyticsSummaryDto[],
): AnalyticsDashboardDto {
  const currencySegments = summaries.map(toCurrencySegment);
  const primary = currencySegments[0] ?? emptyCurrencySegment();
  return {
    filters,
    marginVisible: true,
    empty: currencySegments.length === 0,
    freshness: {
      status: currencySegments.length === 0 ? "UNAVAILABLE" : "FRESH",
      generatedAt: new Date().toISOString(),
      ...(filters.toDay === "" ? {} : { latestDataDay: filters.toDay }),
    },
    currencySegments,
    funnel: analyticsFunnel(primary),
    outcomes: [
      { id: "verified", label: "Verified", value: primary.verified, href: "/requests-list" },
      {
        id: "review",
        label: "Review required",
        value: primary.reviewRequired,
        href: "/requests-list?status=REVIEW_REQUIRED",
      },
      {
        id: "accepted",
        label: "Accepted",
        value: primary.accepted,
        href: "/requests-list?outcome=APPROVED",
      },
    ],
    topProducts: [],
    topCompetitors: [],
    warnings: [],
  };
}

function toCurrencySegment(summary: AnalyticsSummaryDto): AnalyticsCurrencySegment {
  return { ...summary };
}

function analyticsFunnel(segment: AnalyticsCurrencySegment): AnalyticsFunnelStep[] {
  return [
    { id: "requests", label: "Requests", value: segment.requests, href: "/requests-list" },
    { id: "verified", label: "Verified", value: segment.verified, href: "/requests-list" },
    { id: "offers", label: "Offers", value: segment.offers, href: "/requests-list" },
    { id: "accepted", label: "Accepted", value: segment.accepted, href: "/analytics-overview" },
    { id: "ordered", label: "Ordered", value: segment.ordered, href: "/analytics-overview" },
  ];
}

function emptyCurrencySegment(): AnalyticsCurrencySegment {
  return {
    currency: "USD",
    requests: 0,
    verified: 0,
    reviewRequired: 0,
    offers: 0,
    accepted: 0,
    ordered: 0,
    recoveredRevenueMinor: 0,
    discountCostMinor: 0,
    refundedMinor: 0,
    acceptanceRate: 0,
    conversionRate: 0,
  };
}

function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}

function query(filters: AnalyticsFilters): string {
  return new URLSearchParams(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => [key, String(value)]),
  ).toString();
}
